/**
 * TEAM LOGIN COMPONENT
 * ====================
 *
 * Page de connexion pour accéder aux environnements dev/acceptance.
 *
 * Fonctionnalités:
 * - Authentification Firebase (Google, Email/Password)
 * - Redirection automatique après login
 * - Gestion des erreurs de connexion
 * - Design responsive et moderne
 */

import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { Auth, authState, signInWithPopup, signInWithEmailAndPassword, signOut, GoogleAuthProvider, User } from '@angular/fire/auth';
import { TeamAccessService } from '../../../core/services/team-access.service';
import { safeInternalReturnUrl } from '../../../core/utils/return-url.util';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-team-login',
    templateUrl: './team-login.component.html',
    styleUrls: ['./team-login.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class TeamLoginComponent implements OnInit {

  returnUrl: string = '/';
  errorMessage: string = '';
  loading: boolean = false;

  // Email/Password login
  email: string = '';
  password: string = '';

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private auth: Auth,
    private router: Router,
    private route: ActivatedRoute,
    private teamAccess: TeamAccessService
  ) {}

  ngOnInit(): void {
    // Récupérer l'URL de retour depuis les query params
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    // Si déjà authentifié, rediriger.
    // #1976 — abonnement libéré à la destruction : sinon chaque passage sur
    // cette page laissait un écouteur qui, à chaque connexion ultérieure,
    // naviguait vers SON `returnUrl`, en concurrence avec le parcours réel.
    authState(this.auth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async user => {
      if (user && await this.isUserAuthorized(user)) {
        console.log('✅ Utilisateur déjà authentifié et autorisé');
        this.goToReturnUrl();
      }
    });
  }

  /**
   * Rejoue la destination d'origine — chemin ET query string.
   *
   * ⚠️ `navigateByUrl`, jamais `navigate([url])` : passé en COMMANDE, un
   * `returnUrl` qui contient une query string voit son `?` encodé en `%3F`
   * (Angular traite la chaîne comme un segment de chemin) et la destination
   * devient une 404. `TeamOnlyGuard` construit son `returnUrl` avec
   * `state.url`, qui porte chemin + query + fragment (#1140 PR1) : c'est
   * exactement le cas des liens gardés depuis #1173 — `/ma-commande?code=…`
   * (SMS), `/thank-you?session_id=…` (retour Stripe), `/heavy-quote-accepted?…`
   * (e-mail). Sans ça, le verrou de pré-production mangerait le paramètre qui
   * porte la commande.
   *
   * Le `returnUrl` vient d'un query param : on n'accepte qu'un chemin interne
   * (`/x`), jamais `//host` ni une URL absolue.
   */
  private goToReturnUrl(): void {
    // #1315 — la règle « chemin interne uniquement » vit maintenant dans
    // `core/utils/return-url.util.ts`, partagée avec les écrans 2FA qui la
    // jetaient. Comportement inchangé ici.
    this.router.navigateByUrl(safeInternalReturnUrl(this.returnUrl, '/'));
  }

  /**
   * Connexion avec Google
   */
  async loginWithGoogle(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(this.auth, provider);

      if (result.user && await this.isUserAuthorized(result.user)) {
        console.log(`✅ Connexion réussie: ${result.user.email}`);
        this.goToReturnUrl();
      } else {
        console.warn(`🚫 Email non autorisé: ${result.user?.email}`);
        this.errorMessage = 'Votre email n\'est pas autorisé à accéder à cet environnement.';
        await signOut(this.auth);
      }
    } catch (error: any) {
      console.error('❌ Erreur connexion Google:', error);
      this.errorMessage = `Erreur de connexion: ${error.message}`;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Connexion avec Email/Password
   */
  async loginWithEmail(): Promise<void> {
    if (!this.email || !this.password) {
      this.errorMessage = 'Veuillez remplir tous les champs.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const result = await signInWithEmailAndPassword(this.auth, this.email, this.password);

      if (result.user && await this.isUserAuthorized(result.user)) {
        console.log(`✅ Connexion réussie: ${result.user.email}`);
        this.goToReturnUrl();
      } else {
        console.warn(`🚫 Email non autorisé: ${result.user?.email}`);
        this.errorMessage = 'Votre email n\'est pas autorisé à accéder à cet environnement.';
        await signOut(this.auth);
      }
    } catch (error: any) {
      console.error('❌ Erreur connexion Email:', error);

      // Messages d'erreur Firebase personnalisés
      if (error.code === 'auth/user-not-found') {
        this.errorMessage = 'Aucun compte trouvé avec cet email.';
      } else if (error.code === 'auth/wrong-password') {
        this.errorMessage = 'Mot de passe incorrect.';
      } else if (error.code === 'auth/invalid-email') {
        this.errorMessage = 'Format d\'email invalide.';
      } else {
        this.errorMessage = `Erreur: ${error.message}`;
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * 🎯 #338 : vérification serveur (CF checkTeamAccess) — la whitelist n'est
   * plus embarquée dans le bundle. Raccourci local uniquement pour le domaine
   * @befret.be (non sensible).
   */
  private async isUserAuthorized(user: User): Promise<boolean> {
    const email = user.email || '';
    if (email.endsWith('@befret.be')) {
      return true;
    }
    return this.teamAccess.isTeamMember(user.uid);
  }
}
